// Client-side error tracking

interface ErrorLog {
  message: string;
  stack?: string;
  url: string;
  timestamp: string;
  userAgent: string;
  metadata?: Record<string, any>;
}

class ErrorTracker {
  private endpoint = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-error`;
  private apiKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  // SECURITY: Scrub sensitive data before logging
  private scrubSensitiveData(text: string): string {
    return text
      .replace(/\b[\w\.-]+@[\w\.-]+\.\w{2,}\b/gi, '[EMAIL_REDACTED]')
      .replace(/\b(?:password|token|api[_-]?key|secret|auth)["\s:=]+[\w\-\.]+/gi, '[SENSITIVE_REDACTED]')
      .replace(/Bearer\s+[\w\-\.]+/gi, 'Bearer [TOKEN_REDACTED]')
      .replace(/\b\d{13,19}\b/g, '[CC_REDACTED]');
  }

  async logError(error: Error | string, metadata?: Record<string, any>) {
    const message = typeof error === 'string' ? error : error.message;
    const stack = typeof error === 'object' ? error.stack : undefined;

    const errorLog: ErrorLog = {
      message: this.scrubSensitiveData(message),
      stack: stack ? this.scrubSensitiveData(stack) : undefined,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      metadata: metadata ? JSON.parse(this.scrubSensitiveData(JSON.stringify(metadata))) : undefined,
    };

    try {
      await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify(errorLog),
      });
    } catch (logError) {
      // Fail silently in production to avoid cascading errors
      if (import.meta.env.DEV) {
        console.error('Failed to log error:', logError);
      }
    }
  }

  captureException(error: Error, context?: string) {
    // Log to console in development only
    if (import.meta.env.DEV) {
      console.error(`Error in ${context}:`, error);
    }
    this.logError(error, { context });
  }

  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
    // Only log errors, not info/warnings
    if (level === 'error') {
      if (import.meta.env.DEV) {
        console.error(`[ERROR] ${message}`);
      }
      this.logError(message, { level });
    }
  }
}

export const errorTracker = new ErrorTracker();

// Setup global error handlers
export const setupErrorTracking = () => {
  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    errorTracker.captureException(
      new Error(event.reason),
      'unhandledrejection'
    );
  });

  // Global errors
  window.addEventListener('error', (event) => {
    errorTracker.captureException(
      event.error || new Error(event.message),
      'global_error'
    );
  });

  // SECURITY FIX: Removed console.error override to prevent logging sensitive data
  // Use errorTracker.captureException() explicitly where needed instead
};
